import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EndPoints, Order } from '../../../common/enums/enums';
import { errorResponseHelper } from '../../../helpers/apiResponseHelpers';
import { amplifyApi } from '../../../hooks/amplifyApi';
import { newOrderActionTypes } from '../../types';

export const clearNewOrder = createAction(newOrderActionTypes.clearNewOrder);

export const discardOrder = createAction(newOrderActionTypes.discardOrder);

export const clearError = createAction(newOrderActionTypes.clearError);

export const setObservations = createAction(
  newOrderActionTypes.setObservations
);

export const addSpecialRequest = createAction(
  newOrderActionTypes.addSpecialRequest
);

export const setAlternativeOwner = createAction(
  newOrderActionTypes.setAlternativeOwner
);

export const setPromiseDate = createAction(newOrderActionTypes.setPromiseDate);

export const updateCreatedDate = createAction(
  newOrderActionTypes.updateCreatedDate
);

export const changeCompletionType = createAction(
  newOrderActionTypes.changeCompletionType
);

export const removeSpecialRequest = createAction(
  newOrderActionTypes.removeSpecialRequest
);

export const openEditVehicleModal = createAction(
  newOrderActionTypes.openEditVehicleModal
);

export const closeEditVehicleModal = createAction(
  newOrderActionTypes.closeEditVehicleModal
);

export const setSelectedVendor = createAction(
  newOrderActionTypes.setSelectedVendor
);

export const addPriceToSelectedPrices = createAction(
  newOrderActionTypes.addPriceToSelectedPrices
);

export const removePriceToSelectedPrices = createAction(
  newOrderActionTypes.removePriceToSelectedPrices
);

export const getServicesByDepartment = createAsyncThunk(
  newOrderActionTypes.getServicesByDepartment,
  async ({ vendorId, department: { departmentId } }) => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.ENDPOINT.replace(
        '{vendorId}',
        vendorId
      ).replace('{departmentId}', departmentId),
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.METHOD
    );
  }
);

export const setAllDealers = createAsyncThunk(
  newOrderActionTypes.setAllDealers,
  async companyId => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.DEALERS.GET.ENDPOINT.replace('{companyId}', companyId),
      EndPoints.ORDERS.DEALERS.GET.TYPE
    );
  }
);

export const setStockNumber = createAction(newOrderActionTypes.setStockNumber);

export const getVehicleByVin = createAsyncThunk(
  newOrderActionTypes.getVehicleByVin,
  async ({ vin }) => {
    return await getVehicle(vin);
  }
);

export const saveNewOrder = createAsyncThunk(
  newOrderActionTypes.saveNewOrder,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        payload,
        EndPoints.ORDERS.NEW_ORDER.SAVE.ENDPOINT,
        EndPoints.ORDERS.NEW_ORDER.SAVE.METHOD,
        true
      );
      return response.data;
    } catch (error) {
      const woDetails = error.response.data.data.activeOrders
        ? error.response.data.data.activeOrders[0]
        : error.response.data.data;

      return rejectWithValue({
        ...errorResponseHelper(error),
        data: { ...woDetails },
      });
    }
  }
);

export const editVehicleDetails = createAsyncThunk(
  newOrderActionTypes.editVehicleDetails,
  async ({ data, vehicleId }) => {
    await amplifyApi(
      {
        make: data.make,
        model: data.model,
        year: data.year,
        color: data.color,
      },
      EndPoints.ORDERS.NEW_ORDER.EDIT_VEHICLE_DETAIL.ENDPOINT.replace(
        '{vehicleId}',
        vehicleId
      ),
      EndPoints.ORDERS.NEW_ORDER.EDIT_VEHICLE_DETAIL.METHOD
    );

    return await getVehicle(data.vin);
  }
);

export const addVehicleDetails = createAsyncThunk(
  newOrderActionTypes.addVehicleDetails,
  async ({ data }) => {
    return await amplifyApi(
      {
        make: data.make,
        model: data.model,
        year: data.year,
        color: data.color,
        vin: data.vin,
      },
      EndPoints.ORDERS.NEW_ORDER.ADD_VEHICLE_DETAIL.ENDPOINT,
      EndPoints.ORDERS.NEW_ORDER.ADD_VEHICLE_DETAIL.METHOD
    );
  }
);

const getVehicle = vin =>
  amplifyApi(
    {},
    EndPoints.ORDERS.NEW_ORDER.GET_VEHICLE_BY_VIN.ENDPOINT.replace(
      '{vin}',
      vin
    ),
    EndPoints.ORDERS.NEW_ORDER.GET_VEHICLE_BY_VIN.METHOD
  );

export const alternativeOwner = createAsyncThunk(
  newOrderActionTypes.alternativeOwner,
  async ({
    companyId,
    departmentId,
    pageSize = 100,
    pageNumber = 1,
    order = Order.ASC,
    filters = [],
    searchQuery = '',
  }) => {
    return await amplifyApi(
      {
        pageSize,
        pageNumber,
        order,
        filters,
        searchQuery,
        groupedFilters: {
          AND: [
            {
              AND: [
                {
                  column: 'companyId',
                  value: companyId,
                  operator: 'eq',
                },
                {
                  column: 'departmentId',
                  value: departmentId,
                  operator: 'eq',
                },
                {
                  column: 'status',
                  value: 'ACTIVE',
                  operator: 'eq',
                },
              ],
            },
          ],
        },
      },
      EndPoints.USER.ALTERNATIVE_OWNER.ENDPOINT,
      EndPoints.USER.ALTERNATIVE_OWNER.METHOD
    );
  }
);
